import React from 'react';
import { Grid, FormControlLabel, Checkbox, TextField, Autocomplete } from '@mui/material';
import { bridgingPlans, labs, imaging, positioningOptions } from '../constants/options';
import { getCurrentDateTime, getTodayDate } from '../hooks/useFormValidation';

export const Anticoagulation = ({ formData, setFormData, handleChange, validationErrors }) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox name="onAnticoagulants" checked={formData.onAnticoagulants} onChange={handleChange} />}
          label="On Anticoagulants"
        />
      </Grid>
      {formData.onAnticoagulants && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              name="lastDose"
              type="datetime-local"
              label="Last Dose Date/Time"
              value={formData.lastDose || ''}
              inputProps={{ max: getCurrentDateTime() }}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!validationErrors.lastDose}
              helperText={validationErrors.lastDose || "Cannot be in the future"}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={bridgingPlans}
              value={formData.bridgingPlan || null}
              onChange={(e, newValue) => setFormData(prev => ({ ...prev, bridgingPlan: newValue || '' }))}
              renderInput={(params) => <TextField {...params} label="Bridging Plan" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="stopDate"
              label="Stop Date"
              type="date"
              value={formData.stopDate || ''}
              inputProps={{
                min: getTodayDate(),
                max: formData.scheduledDatetime ? formData.scheduledDatetime.slice(0, 10) : undefined
              }}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!validationErrors.stopDate}
              helperText={validationErrors.stopDate || "Auto-suggested based on surgery date"}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox name="postOpThromboprophylaxis" checked={formData.postOpThromboprophylaxis} onChange={handleChange} />}
              label="Thromboprophylaxis Needed Post-Op"
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <TextField
          name="allergyReview"
          label="Allergy Review"
          value={formData.allergyReview || ''}
          onChange={handleChange}
          fullWidth
          multiline
          rows={2}
          placeholder="Document any known allergies or adverse reactions"
        />
      </Grid>
    </Grid>
  );
};

export const Investigations = ({ formData, setFormData, handleChange }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
            <Autocomplete
                multiple
                options={labs}
                value={formData.labsToDo || []}
                onChange={(e, newVal) => setFormData(prev => ({ ...prev, labsToDo: Array.isArray(newVal) ? newVal : [] }))}
                renderInput={(params) => <TextField {...params} label="Labs to be Done" />}
            />
            </Grid>
            <Grid item xs={12}>
            <Autocomplete
                multiple
                options={imaging}
                value={formData.imagingToBeAvailable || []}
                onChange={(e, newVal) => setFormData(prev => ({ ...prev, imagingToBeAvailable: Array.isArray(newVal) ? newVal : [] }))}
                renderInput={(params) => <TextField {...params} label="Imaging to be Available in OT" />}
            />
            </Grid>
            <Grid item xs={12}>
            <FormControlLabel
                control={<Checkbox name="cardiologyClearance" checked={formData.cardiologyClearance} onChange={handleChange} />}
                label="Cardiology Clearance Required"
            />
            </Grid>
            <Grid item xs={12}>
            <FormControlLabel
                control={<Checkbox name="anesthClearance" checked={formData.anesthClearance} onChange={handleChange} />}
                label="Anesthesiology Clearance Required"
            />
            </Grid>
            <Grid item xs={12}>
            <TextField
                name="otherSpecialtyClearance"
                label="Other Specialty Clearance"
                value={formData.otherSpecialtyClearance || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
            />
            </Grid>
        </Grid>
    );
};

export const SurgeonInstructions = ({ formData, setFormData, handleChange, validationErrors }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
            <TextField
                name="additionalEquipments"
                label="Additional Equipment Needed"
                value={formData.additionalEquipments || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
            />
            </Grid>
            <Grid item xs={12}>
            <TextField
                name="criticalNotes"
                label="Critical Considerations"
                value={formData.criticalNotes || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Important notes for the surgical team"
            />
            </Grid>
            <Grid item xs={12}>
            <TextField
                name="estimatedBloodLoss"
                type="number"
                label="Estimated Blood Loss (ml)"
                value={formData.estimatedBloodLoss || ''}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, max: 10000 }}
                error={!!validationErrors.estimatedBloodLoss}
                helperText={validationErrors.estimatedBloodLoss || "Enter estimated blood loss in milliliters"}
            />
            </Grid>
            <Grid item xs={12}>
            <FormControlLabel
                control={<Checkbox name="siteMarking" checked={formData.siteMarking} onChange={handleChange} />}
                label="Surgical Site Marking Required"
            />
            </Grid>
            <Grid item xs={12}>
            <Autocomplete
                options={positioningOptions}
                value={formData.positioning || null}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, positioning: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="Positioning Requirement" />}
            />
            </Grid>
            <Grid item xs={12}>
            <TextField
                name="surgeonNotes"
                label="Other Instructions"
                value={formData.surgeonNotes || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
            />
            </Grid>
        </Grid>
    );
};
